import { Text } from "../../../../atoms/Text";
import { cn } from "../../../../lib/cn";
import { hideBelowClass, hideBelowWidthClass } from "../collapse";
import { TITLE_FLEX, type LedgerCollapse, type LedgerColumn } from "../types";

export interface LedgerHeaderProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** Label over the title cell. */
  titleLabel?: string;
  valueLabel?: string;
  showValues?: boolean;
  /** Reserve the same trailing slot used by row actions. */
  showActions?: boolean;
  /** Hide the column header once rows switch to compact metadata. */
  hideBelow?: number;
}

/** Column headings, honouring the same collapse point and value toggle as rows. */
const headerCellClass =
  "font-medium text-(length:--ledger-text-cell) leading-[1.66]";

export const LedgerHeader = ({
  columns,
  collapse,
  titleLabel = "Work",
  valueLabel = "Value",
  showValues = true,
  showActions = false,
  hideBelow,
}: LedgerHeaderProps) => (
  <div
    className={cn(
      "flex items-center gap-3 border-b border-border-default px-4 py-1.5",
      hideBelow !== undefined &&
        hideBelowWidthClass(collapse.container, hideBelow),
    )}
  >
    <div className="min-w-0" style={{ flex: `${TITLE_FLEX} 1 0` }}>
      <Text
        as="div"
        variant="inherit"
        color="secondary"
        className={headerCellClass}
      >
        {titleLabel}
      </Text>
    </div>
    {columns.map((column) => (
      <div
        key={column.key}
        className={cn(
          "min-w-0",
          column.hideBelow === undefined
            ? hideBelowClass(collapse)
            : hideBelowWidthClass(collapse.container, column.hideBelow),
          column.align === "center" && "text-center",
          column.align === "end" && "text-right",
        )}
        style={{ flex: `${column.flex} 1 0` }}
      >
        <Text
          as="div"
          variant="inherit"
          color="secondary"
          className={headerCellClass}
        >
          {column.label}
        </Text>
      </div>
    ))}
    {showValues && (
      <div className="w-(--ledger-value-width) shrink-0 text-right">
        <Text
          as="div"
          variant="inherit"
          color="secondary"
          className={headerCellClass}
        >
          {valueLabel}
        </Text>
      </div>
    )}
    {showActions ? <div aria-hidden="true" className="w-9 shrink-0" /> : null}
  </div>
);
